"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.hasDetailedReasonCode = exports.addGapFiltersToDataRequirement = exports.calculateReasonDetail = exports.generateGapsInCareBundle = exports.generateReasonCode = exports.generateGuidanceResponses = exports.getOrExpressionFromStack = exports.groupGapQueries = exports.generateDetectedIssueResources = exports.processQueriesForGaps = void 0;
const lodash_1 = __importDefault(require("lodash"));
const uuid_1 = require("uuid");
const Enums_1 = require("../types/Enums");
const GracefulError_1 = require("../types/errors/GracefulError");
const ValueComparisonHelpers_1 = require("../helpers/ValueComparisonHelpers");
const QueryFilterParser_1 = require("../helpers/elm/QueryFilterParser");
/**
 * Iterate through base queries and add clause results for parent query and retrieve
 *
 * @param queries base queries parsed from ELM
 * @param detailedResult execution results for this patient
 * @returns same query info but with info about clause results for query and retrieve
 */
function processQueriesForGaps(queries, detailedResult) {
    return queries.map(q => {
        var _a, _b;
        // Determine satisfaction of parent query and leaf node retrieve
        const parentQueryResult = (_a = detailedResult.clauseResults) === null || _a === void 0 ? void 0 : _a.find(cr => cr.libraryName === q.queryLibraryName && cr.localId === q.queryLocalId);
        const retrieveResult = (_b = detailedResult.clauseResults) === null || _b === void 0 ? void 0 : _b.find(cr => cr.libraryName === q.retrieveLibraryName && cr.localId === q.retrieveLocalId);
        const parentQueryHasResult = (parentQueryResult === null || parentQueryResult === void 0 ? void 0 : parentQueryResult.final) === Enums_1.FinalResult.TRUE;
        const retrieveHasResult = (retrieveResult === null || retrieveResult === void 0 ? void 0 : retrieveResult.final) === Enums_1.FinalResult.TRUE;
        const gapQuery = Object.assign(Object.assign({}, q), { parentQueryHasResult,
            retrieveHasResult });
        return gapQuery;
    });
}
exports.processQueriesForGaps = processQueriesForGaps;
/**
 * Generate a FHIR DetectedIssue resource for Gaps in Care per http://build.fhir.org/ig/HL7/davinci-deqm/StructureDefinition-gaps-detectedissue-deqm.html
 *
 * @param queries numerator queries from a call to findRetrieves
 * @param measureReport FHIR MeasureReport to be referenced by the issue
 */
function generateDetectedIssueResources(queries, measureReport, improvementNotation) {
    const relevantGapQueries = queries.filter(q => 
    // If positive improvement, we want queries with results as gaps. Vice versa for negative
    improvementNotation === Enums_1.ImprovementNotation.POSITIVE ? !q.parentQueryHasResult : q.parentQueryHasResult);
    const groupedQueries = groupGapQueries(relevantGapQueries);
    const guidanceResponses = groupedQueries.map(q => generateGuidanceResponses(q, measureReport.measure, improvementNotation));
    const formattedResponses = guidanceResponses.map(gr => {
        // Uniquify a DetectedIssue's contained GuidanceResponses by deep equality on reasonCode and dataRequirement
        // a duplicate here indicates the exact same gap that could theoretically be parsed from different measure logic clauses
        const filteredGrs = lodash_1.default.uniqWith(gr.guidanceResponses, (gr1, gr2) => lodash_1.default.isEqual(gr1.dataRequirement, gr2.dataRequirement) && lodash_1.default.isEqual(gr1.reasonCode, gr2.reasonCode));
        return {
            resourceType: 'DetectedIssue',
            id: (0, uuid_1.v4)(),
            status: 'final',
            code: {
                coding: [
                    {
                        system: 'http://terminology.hl7.org/CodeSystem/detectedissue-category',
                        code: 'care-gap',
                        display: 'Gap in Care Detected'
                    }
                ]
            },
            evidence: filteredGrs.map(gr => {
                return {
                    detail: [{ reference: `#${gr.id}` }]
                };
            }),
            contained: filteredGrs
        };
    });
    //accumulate all error info into a single array
    const errorInfo = guidanceResponses.reduce((acc, e) => {
        acc.push(...e.withErrors);
        return acc;
    }, []);
    return { detectedIssues: formattedResponses, withErrors: errorInfo };
}
exports.generateDetectedIssueResources = generateDetectedIssueResources;
/**
 * "Groups" gaps queries that are functionally the same in terms of putting a patient into the improvement population
 * (whether that is the numerator or the denominator, depending on the improvement notation). For the moment, only handles
 * "or"-d queries, where any one of the listed DataTypeQuery objects would put the patient into the improvement population.
 *
 * @param queries list of queries from the execution engine.
 * @returns an array of arrays of type DataTypeQuery
 */
function groupGapQueries(queries) {
    const queryGroups = new Map();
    const ungroupedQueries = [];
    queries.forEach((q) => {
        var _a;
        // Logic to determine grouped queries. Will likely get more complex
        // as query grouping evolves
        const stackOrEntry = getOrExpressionFromStack(q.expressionStack);
        if (stackOrEntry) {
            if (queryGroups.get(stackEntryString(stackOrEntry))) {
                // If we've already started a group for these queries, add to the group
                (_a = queryGroups.get(stackEntryString(stackOrEntry))) === null || _a === void 0 ? void 0 : _a.push(q);
            }
            else {
                // Otherwise, start a new group
                queryGroups.set(stackEntryString(stackOrEntry), [q]);
            }
        }
        else {
            // collect queries that aren't part of a grouping
            ungroupedQueries.push([q]);
        }
    });
    return Array.from(queryGroups.values()).concat(ungroupedQueries);
}
exports.groupGapQueries = groupGapQueries;
/**
 * Detects scenario in expressionStack where this query is part of an "Or"
 * i.e. Either the first entry in the stack is an "Or," or it is an "ExpressionRef" right on top of an "Or"
 *
 * @param expressionStack the current expression stack for this query
 * @returns Matching "Or", or null if not found
 */
function getOrExpressionFromStack(expressionStack) {
    if (expressionStack && expressionStack.length >= 2) {
        if (expressionStack[0].type === 'ExpressionRef') {
            return expressionStack[1].type === 'Or' ? expressionStack[1] : null;
        }
        return expressionStack[0].type === 'Or' ? expressionStack[0] : null;
    }
    return null;
}
exports.getOrExpressionFromStack = getOrExpressionFromStack;
function stackEntryString(entry) {
    return JSON.stringify(entry);
}
function didEncounterDetailedValueFilterErrors(tbd) {
    if (tbd.message) {
        return true;
    }
    else {
        return false;
    }
}
/**
 * Generate FHIR GuidanceResponse resources for queries that are gaps
 *
 * @param queries list of all queries that are gaps for this measure
 * @param measureURL fully qualified URL referencing the measure
 * @param improvementNotation ImprovementNotation.POSITIVE or ImprovementNotation.NEGATIVE
 * @returns list of FHIR GuidanceResponse resources with detailed gaps information
 */
function generateGuidanceResponses(queries, measureURL, improvementNotation) {
    const withErrors = [];
    const guidanceResponses = queries.map(q => {
        var _a;
        const dataTypeCodeFilter = {
            path: 'code'
        };
        if (q.valueSet) {
            dataTypeCodeFilter.valueSet = q.valueSet;
        }
        else if (q.code) {
            dataTypeCodeFilter.code = [
                {
                    code: q.code.code,
                    system: q.code.system
                }
            ];
        }
        let gapStatus;
        if (((_a = q.reasonDetail) === null || _a === void 0 ? void 0 : _a.hasReasonDetail) && q.reasonDetail.reasons.length > 0) {
            gapStatus = q.reasonDetail.reasons.map(generateReasonCode);
        }
        else {
            const gapCoding = improvementNotation === Enums_1.ImprovementNotation.POSITIVE
                ? [
                    {
                        system: 'http://hl7.org/fhir/us/davinci-deqm/CodeSystem/care-gap-reason',
                        code: Enums_1.CareGapReasonCode.NOTFOUND,
                        display: Enums_1.CareGapReasonCodeDisplay[Enums_1.CareGapReasonCode.NOTFOUND]
                    }
                ]
                : [
                    {
                        system: 'http://hl7.org/fhir/us/davinci-deqm/CodeSystem/care-gap-reason',
                        code: Enums_1.CareGapReasonCode.PRESENT,
                        display: Enums_1.CareGapReasonCodeDisplay[Enums_1.CareGapReasonCode.PRESENT]
                    }
                ];
            gapStatus = [
                {
                    coding: gapCoding
                }
            ];
        }
        const dataRequirement = {
            type: q.dataType,
            codeFilter: [Object.assign({}, dataTypeCodeFilter)]
        };
        addGapFiltersToDataRequirement(q, dataRequirement, withErrors);
        const guidanceResponse = {
            resourceType: 'GuidanceResponse',
            id: (0, uuid_1.v4)(),
            dataRequirement: [dataRequirement],
            reasonCode: gapStatus,
            status: 'data-required',
            moduleUri: measureURL
        };
        return guidanceResponse;
    });
    // Prefer GRs to be sorted by ones with more specific reasonCodes other than PRESENT or MISSING
    guidanceResponses.sort((gr1, gr2) => {
        if (hasDetailedReasonCode(gr1)) {
            return -1;
        }
        else if (hasDetailedReasonCode(gr2)) {
            return 1;
        }
        return 0;
    });
    return { guidanceResponses, withErrors };
}
exports.generateGuidanceResponses = generateGuidanceResponses;
/**
 * Creates a FHIR CodeableConcept object representing a reason detail for the GuidanceResponse resource.
 *
 * @param reason The reason detail data for a single reason.
 * @returns The FHIR CodeableConcept object to add to the GuidanceResponse.reasonCode field.
 */
function generateReasonCode(reason) {
    var _a;
    const reasonCoding = {
        system: 'http://hl7.org/fhir/us/davinci-deqm/CodeSystem/care-gap-reason',
        code: reason.code,
        display: Enums_1.CareGapReasonCodeDisplay[reason.code]
    };
    const reasonCode = {
        coding: [reasonCoding]
    };
    // If there is a referenced resource create and add the extension
    if (reason.reference) {
        const detailExt = {
            url: 'http://hl7.org/fhir/us/davinci-deqm/StructureDefinition/reasonDetail',
            extension: [
                {
                    url: 'reference',
                    valueReference: {
                        reference: reason.reference
                    }
                }
            ]
        };
        if (reason.path) {
            (_a = detailExt.extension) === null || _a === void 0 ? void 0 : _a.push({
                url: 'path',
                valueString: reason.path
            });
        }
        reasonCode.extension = [detailExt];
    }
    return reasonCode;
}
exports.generateReasonCode = generateReasonCode;
/**
 * Generate a Gaps in Care Bundle resource per http://build.fhir.org/ig/HL7/davinci-deqm/StructureDefinition-gaps-bundle-deqm.html
 *
 * @param detectedIssue FHIR DetectedIssue generated during gaps
 * @param measureReport FHIR MeasureReport generated during calculation
 * @param patient Current FHIR Patient processed in execution
 */
function generateGapsInCareBundle(detectedIssues, measureReport, patient) {
    const composition = {
        resourceType: 'Composition',
        type: {
            coding: [
                {
                    system: 'http://hl7.org/fhir/us/davinci-deqm/CodeSystem/gaps-doc-type',
                    code: 'gaps-doc',
                    display: 'Gaps in Care Report'
                }
            ]
        },
        author: [Object.assign({}, measureReport.subject)],
        subject: measureReport.subject,
        date: new Date().toISOString(),
        title: 'Gaps in Care Report',
        status: 'preliminary',
        section: [
            {
                title: measureReport.measure,
                focus: {
                    reference: `MeasureReport/${measureReport.id}`
                },
                entry: detectedIssues.map(i => {
                    return { reference: `DetectedIssue/${i.id}` };
                })
            }
        ]
    };
    const returnBundle = {
        resourceType: 'Bundle',
        type: 'document',
        entry: [
            {
                resource: composition
            },
            {
                resource: measureReport
            },
            {
                resource: patient
            }
        ]
    };
    detectedIssues.forEach(i => {
        var _a;
        (_a = returnBundle.entry) === null || _a === void 0 ? void 0 : _a.push({ resource: i });
    });
    return returnBundle;
}
exports.generateGapsInCareBundle = generateGapsInCareBundle;
/**
 * Add reason detail data to each DataTypeQuery passed in
 *
 * @param retrieves numerator queries from a call to findRetrieves
 * @param improvementNotation string indicating positive or negative improvement notation for the measure being used
 * @param detailedResult result from calculation to look up clause values
 * @return mapped list of queries with reason detail info if relevant
 */
function calculateReasonDetail(retrieves, improvementNotation, detailedResult) {
    const withErrors = [];
    const isPositiveImprovement = improvementNotation === Enums_1.ImprovementNotation.POSITIVE;
    const results = retrieves.map(r => {
        var _a, _b, _c;
        const reasonDetail = {
            hasReasonDetail: false,
            reasons: []
        };
        // If this is a positive improvement notation measure then we can look for reasons why the query wasn't satisfied
        let shouldCalculateReasonDetail = false;
        if (((_a = r.queryInfo) === null || _a === void 0 ? void 0 : _a.fromExternalClause) === true) {
            // Clause doing the comparison of values
            const valueClauseResult = (_b = detailedResult === null || detailedResult === void 0 ? void 0 : detailedResult.clauseResults) === null || _b === void 0 ? void 0 : _b.find(cr => cr.libraryName === r.retrieveLibraryName && cr.localId === r.valueComparisonLocalId);
            // reasonDetail occurs in this cause if the clause doing the comparison of some value
            // has a truthy result for negative imporve, falsy for pos,
            shouldCalculateReasonDetail = isPositiveImprovement
                ? (valueClauseResult === null || valueClauseResult === void 0 ? void 0 : valueClauseResult.final) === Enums_1.FinalResult.FALSE
                : (valueClauseResult === null || valueClauseResult === void 0 ? void 0 : valueClauseResult.final) === Enums_1.FinalResult.TRUE;
        }
        else {
            // Else, clause has come from the where part of a query
            // compute reasonDetail if the overall query was truthy for negative improve, falsy for pos
            shouldCalculateReasonDetail = shouldCalculateReasonDetail = isPositiveImprovement
                ? r.parentQueryHasResult === false
                : r.parentQueryHasResult === true;
        }
        // If we detect more going on, set the boolean to true
        // NOTE: this may still result in a basic reason code, but it allows the engine to recognize
        // that more information was processed
        reasonDetail.hasReasonDetail = shouldCalculateReasonDetail;
        if (shouldCalculateReasonDetail === true) {
            if (r.queryInfo && (detailedResult === null || detailedResult === void 0 ? void 0 : detailedResult.clauseResults)) {
                const flattenedFilters = (0, QueryFilterParser_1.flattenFilters)(r.queryInfo.filter);
                const resources = (_c = detailedResult.clauseResults) === null || _c === void 0 ? void 0 : _c.find(cr => cr.libraryName === r.retrieveLibraryName && cr.localId === r.retrieveLocalId);
                if (resources) {
                    // Compute reason detail for every filter present on the found raw data from a retrieve
                    resources.raw.forEach((resource) => {
                        flattenedFilters.forEach(f => {
                            var _a, _b, _c, _d, _e;
                            // ValueFilters are handled for both positive and negative improvement measures
                            if (f.type === 'value') {
                                const valueFilter = f;
                                const reason = {
                                    path: valueFilter.attribute
                                };
                                // TODO: might need to check if we get an array or a singleton
                                // a query could return either
                                const attrPath = (_a = valueFilter.attribute) === null || _a === void 0 ? void 0 : _a.split('.');
                                // Access desired property of FHIRObject
                                let desiredAttr = resource;
                                attrPath === null || attrPath === void 0 ? void 0 : attrPath.forEach(key => {
                                    if (desiredAttr) {
                                        desiredAttr = desiredAttr[key];
                                    }
                                });
                                let comparisonResult = null;
                                if ((_b = valueFilter.valueQuantity) === null || _b === void 0 ? void 0 : _b.value) {
                                    const quantity = desiredAttr.value;
                                    const actualValue = quantity.value;
                                    const requiredValue = valueFilter.valueQuantity.value;
                                    comparisonResult = (0, ValueComparisonHelpers_1.compareValues)(actualValue, requiredValue, valueFilter.comparator);
                                }
                                else if (valueFilter.valueInteger) {
                                    comparisonResult = (0, ValueComparisonHelpers_1.compareValues)(desiredAttr.value, valueFilter.valueInteger, valueFilter.comparator);
                                }
                                // Resource caused gap
                                if (isPositiveImprovement ? comparisonResult === false : comparisonResult === true) {
                                    reason.reference = `${resource._json.resourceType}/${resource._json.id}`;
                                    reason.code = Enums_1.CareGapReasonCode.VALUEOUTOFRANGE;
                                    reasonDetail.reasons.push(reason);
                                }
                            }
                            else if (isPositiveImprovement && f.type === 'during') {
                                const duringFilter = f;
                                if (duringFilter.valuePeriod.interval) {
                                    const path = duringFilter.attribute.split('.');
                                    const interval = duringFilter.valuePeriod.interval;
                                    // Access desired property of FHIRObject
                                    let desiredAttr = resource;
                                    const foundPath = [];
                                    for (const key of path) {
                                        foundPath.push(key);
                                        if (desiredAttr) {
                                            desiredAttr = desiredAttr[key];
                                            /*
                                             There's a chance that the desiredAttr isn't exactly at the described point in
                                             the path. For this reason, just take the first attribute whose value is a Datetime
                                            */
                                            if ((_c = desiredAttr === null || desiredAttr === void 0 ? void 0 : desiredAttr.value) === null || _c === void 0 ? void 0 : _c.isDateTime) {
                                                break;
                                            }
                                        }
                                    }
                                    // Use DateOutOfRange code if data point is outside of the desired interval
                                    if ((_d = desiredAttr === null || desiredAttr === void 0 ? void 0 : desiredAttr.value) === null || _d === void 0 ? void 0 : _d.isDateTime) {
                                        const isAttrContainedInInterval = interval.contains(desiredAttr.value);
                                        if (isAttrContainedInInterval === false) {
                                            reasonDetail.reasons.push({
                                                code: Enums_1.CareGapReasonCode.DATEOUTOFRANGE,
                                                path: foundPath.join('.'),
                                                reference: `${resource._json.resourceType}/${resource.id.value}`
                                            });
                                        }
                                    }
                                    else {
                                        // if the attribute wasn't found then we can consider it NotFound (logical)
                                        reasonDetail.reasons.push({
                                            code: Enums_1.CareGapReasonCode.NOTFOUND,
                                            path: duringFilter.attribute,
                                            reference: `${resource._json.resourceType}/${resource.id.value}`
                                        });
                                    }
                                }
                            }
                            else if (isPositiveImprovement && f.type === 'notnull') {
                                const notNullFilter = f;
                                const attrPath = notNullFilter.attribute.split('.');
                                // Access desired property of FHIRObject
                                let desiredAttr = resource;
                                attrPath.forEach(key => {
                                    if (desiredAttr) {
                                        desiredAttr = desiredAttr[key];
                                    }
                                });
                                /*
                                  Use NotFound code if data is null. NotFound is used both when the desired resource
                                  is not found and when the desired resource is found, but the desired attribute is
                                  missing from it
                                */
                                if (desiredAttr === null || desiredAttr === undefined) {
                                    reasonDetail.reasons.push({
                                        code: Enums_1.CareGapReasonCode.NOTFOUND,
                                        path: notNullFilter.attribute,
                                        reference: `${resource._json.resourceType}/${resource.id.value}`
                                    });
                                }
                            }
                            else if (isPositiveImprovement) {
                                // TODO: This logic is not perfect, and can be corrupted by multiple resources spanning truthy values for all filters
                                // For non-during filters, look up clause result by localId
                                // Ideally we can look to modify cql-execution to help us with this flaw
                                const clauseResult = (_e = detailedResult.clauseResults) === null || _e === void 0 ? void 0 : _e.find(cr => cr.libraryName === r.retrieveLibraryName && cr.localId === f.localId);
                                // False clause means this specific filter was falsy
                                if (clauseResult && clauseResult.final === Enums_1.FinalResult.FALSE) {
                                    const code = getGapReasonCode(f);
                                    if (!(0, GracefulError_1.isOfTypeGracefulError)(code)) {
                                        // if this filter is filtering on an attribute of a resource then include info about the path to the
                                        // attribute and reference the patient
                                        if (f.attribute) {
                                            reasonDetail.reasons.push({
                                                code: code,
                                                path: f.attribute,
                                                reference: `${resource._json.resourceType}/${resource.id.value}`
                                            });
                                        }
                                        else {
                                            reasonDetail.reasons.push({ code: code });
                                        }
                                    }
                                    else {
                                        withErrors.push(code);
                                    }
                                }
                            }
                        });
                    });
                }
            }
        }
        // If no specific reason details found, default is NotFound or Present based on ImprovementNotation
        if (reasonDetail.reasons.length === 0) {
            reasonDetail.reasons = isPositiveImprovement
                ? [{ code: Enums_1.CareGapReasonCode.NOTFOUND }]
                : [{ code: Enums_1.CareGapReasonCode.PRESENT }];
        }
        // add the reason detail we calculated to the query info and retrieve and return it
        return Object.assign(Object.assign({}, r), { reasonDetail });
    });
    return { results, withErrors };
}
exports.calculateReasonDetail = calculateReasonDetail;
function getGapReasonCode(filter) {
    switch (filter.type) {
        case 'equals':
        case 'in':
            return Enums_1.CareGapReasonCode.INVALIDATTRIBUTE;
        case 'during':
            return Enums_1.CareGapReasonCode.DATEOUTOFRANGE;
        default:
            return { message: `unknown reasonCode mapping for filter type ${filter.type}` };
    }
}
/**
 *
 * @param q The query which contains the filters to add to the data requirement
 * @param dataRequirement Data requirement to add date filters to
 * @param withErrors Errors object which will eventually be returned to the user if populated
 * @returns void, but populated the dataRequirement filters
 */
function addGapFiltersToDataRequirement(q, dataRequirement, withErrors) {
    if (q.queryInfo) {
        const relevantSource = q.queryInfo.sources.find(source => source.resourceType === q.dataType);
        // if a source cannot be found that matches, exit the function
        if (relevantSource) {
            const detailedFilters = (0, QueryFilterParser_1.flattenFilters)(q.queryInfo.filter);
            detailedFilters.forEach(df => {
                // DuringFilter, etc. inherit from attribute filter (and have alias on them)
                if (relevantSource.alias === df.alias) {
                    if (df.type === 'equals' || df.type === 'in') {
                        const cf = (0, QueryFilterParser_1.generateDetailedCodeFilter)(df, q.dataType);
                        if (cf !== null) {
                            if (dataRequirement.codeFilter) {
                                dataRequirement.codeFilter.push(cf);
                            }
                            else {
                                dataRequirement.codeFilter = [cf];
                            }
                        }
                    }
                    else if (df.type === 'during') {
                        const dateFilter = (0, QueryFilterParser_1.generateDetailedDateFilter)(df);
                        if (dataRequirement.dateFilter) {
                            dataRequirement.dateFilter.push(dateFilter);
                        }
                        else {
                            dataRequirement.dateFilter = [dateFilter];
                        }
                    }
                    else {
                        const valueFilter = (0, QueryFilterParser_1.generateDetailedValueFilter)(df);
                        if (didEncounterDetailedValueFilterErrors(valueFilter)) {
                            withErrors.push(valueFilter);
                        }
                        else if (valueFilter) {
                            if (dataRequirement.extension) {
                                dataRequirement.extension.push(valueFilter);
                            }
                            else {
                                dataRequirement.extension = [valueFilter];
                            }
                        }
                    }
                }
            });
        }
    }
}
exports.addGapFiltersToDataRequirement = addGapFiltersToDataRequirement;
function hasDetailedReasonCode(gr) {
    var _a;
    return (((_a = gr.reasonCode) === null || _a === void 0 ? void 0 : _a.some(c => {
        var _a, _b, _c, _d;
        return ((_b = (_a = c.coding) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.code) !== Enums_1.CareGapReasonCode.NOTFOUND && ((_d = (_c = c.coding) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.code) !== Enums_1.CareGapReasonCode.PRESENT;
    })) || false);
}
exports.hasDetailedReasonCode = hasDetailedReasonCode;
//# sourceMappingURL=GapsReportBuilder.js.map